<template>
  <div class="skills-container">
    <!-- Полноэкранное сохранение -->
    <Transition name="overlay">
      <div
        v-if="isSaving"
        class="loading-overlay"
        role="status"
        aria-live="polite"
      >
        <div class="saving-card">
          <div class="saving-spinner"></div>

          <div class="saving-content">
            <p class="saving-title">Сохраняем навыки</p>
            <p class="saving-subtitle">
              Это займёт всего несколько секунд
            </p>
          </div>
        </div>
      </div>
    </Transition>

    <div class="skills-content">
      <!-- Заголовок -->
      <header class="skills-header">
        <div class="header-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3L14.25 8.15L20 8.75L15.7 12.55L16.95 18.15L12 15.25L7.05 18.15L8.3 12.55L4 8.75L9.75 8.15L12 3Z"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linejoin="round"
            />

            <path
              d="M19 3V6M17.5 4.5H20.5"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <div class="header-text">
          <h2 class="skills-title">
            Добавьте ключевые навыки
          </h2>

          <p class="skills-description">
            Навыки помогут AI точнее подбирать вакансии и рекомендовать
            ваш профиль подходящим работодателям.
          </p>
        </div>
      </header>

      <!-- Выбранные навыки -->
      <Transition name="selected-section">
        <section
          v-if="selectedSkills.length > 0"
          class="selected-skills-section"
        >
          <div class="selected-skills-header">
            <div>
              <p class="section-title">
                Ваши навыки
              </p>

              <p class="section-subtitle">
                {{ selectedSkillsCountText }}
              </p>
            </div>

            <button
              class="clear-all-button"
              type="button"
              @click="clearSelectedSkills"
            >
              Очистить
            </button>
          </div>

          <TransitionGroup
            name="skill-chip"
            tag="div"
            class="selected-skills"
          >
            <button
              v-for="skill in selectedSkills"
              :key="skill.id"
              type="button"
              class="selected-skill"
              :title="`Удалить навык ${skill.name}`"
              @click="removeSkill(skill.id)"
            >
              <span class="selected-skill-name">
                {{ skill.name }}
              </span>

              <span class="remove-icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M7 7L17 17M17 7L7 17"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>
              </span>
            </button>
          </TransitionGroup>
        </section>
      </Transition>

      <!-- Поиск -->
      <section class="search-section">
        <div
          class="search-bar"
          :class="{
            focused: isSearchFocused,
            filled: hasSearchQuery
          }"
        >
          <svg
            class="search-icon"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              stroke-width="1.8"
            />

            <path
              d="M16.2 16.2L21 21"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>

          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Введите навык"
            autocomplete="off"
            spellcheck="false"
            aria-label="Поиск навыков"
            @input="handleSearchInput"
            @focus="isSearchFocused = true"
            @blur="isSearchFocused = false"
            @keydown.enter.prevent="handleEnterPress"
            @keydown.esc.prevent="clearSearch"
          />

          <button
            v-if="hasSearchQuery && !isSearching"
            type="button"
            class="clear-search-button"
            aria-label="Очистить поиск"
            @mousedown.prevent
            @click="clearSearch"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M7 7L17 17M17 7L7 17"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <div
            v-if="isSearching"
            class="search-spinner"
            aria-label="Выполняется поиск"
          ></div>
        </div>

        <p class="search-hint">
          Например: Swift, Python, Figma, управление проектами
        </p>
      </section>

      <!-- Контент -->
      <div class="skills-state-container">
        <Transition name="content-fade" mode="out-in">
          <!-- Начальное состояние -->
          <div
            v-if="!hasSearchQuery"
            key="initial"
            class="initial-state"
          >
            <div class="initial-illustration">
              <div class="illustration-center">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M12 3L14.25 8.15L20 8.75L15.7 12.55L16.95 18.15L12 15.25L7.05 18.15L8.3 12.55L4 8.75L9.75 8.15L12 3Z"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <span class="illustration-chip chip-one">
                Swift
              </span>

              <span class="illustration-chip chip-two">
                Figma
              </span>

              <span class="illustration-chip chip-three">
                Python
              </span>

              <span class="illustration-chip chip-four">
                Продажи
              </span>
            </div>

            <h3 class="state-title">
              Расскажите, что вы умеете
            </h3>

            <p class="state-description">
              Начните вводить название навыка. Вы сможете выбрать его
              из каталога или создать новый.
            </p>

            <div class="examples">
              <span class="examples-label">
                Попробуйте:
              </span>

              <button
                v-for="example in skillExamples"
                :key="example"
                type="button"
                class="example-skill"
                @click="searchExample(example)"
              >
                {{ example }}
              </button>
            </div>
          </div>

          <!-- Загрузка поиска -->
          <div
            v-else-if="isSearching"
            key="loading"
            class="search-loading-state"
          >
            <div class="results-heading">
              <div>
                <p class="results-title">
                  Ищем навыки
                </p>

                <p class="results-subtitle">
                  Проверяем каталог HR3
                </p>
              </div>

              <div class="searching-dots" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <div class="skills-skeleton-list">
              <div
                v-for="index in 8"
                :key="index"
                class="skill-skeleton skeleton-block"
                :class="`skeleton-width-${index}`"
              ></div>
            </div>
          </div>

          <!-- Найденные навыки -->
          <div
            v-else-if="visibleSkills.length > 0"
            key="results"
            class="search-results-state"
          >
            <div class="results-heading">
              <div>
                <p class="results-title">
                  Найденные навыки
                </p>

                <p class="results-subtitle">
                  {{ resultsCountText }}
                </p>
              </div>
            </div>

            <div class="skills-list">
              <button
                v-for="skill in visibleSkills"
                :key="skill.id"
                type="button"
                class="skill-item"
                :class="{ selected: isSelected(skill.id) }"
                @click="toggleSkill(skill)"
              >
                <span class="skill-item-icon">
                  <svg
                    v-if="isSelected(skill.id)"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M5 12.5L9.3 16.5L19 7"
                      stroke="currentColor"
                      stroke-width="2.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 5V19M5 12H19"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>

                <span class="skill-item-name">
                  {{ skill.name }}
                </span>

                <span class="skill-item-status">
                  {{ isSelected(skill.id) ? "Добавлено" : "Добавить" }}
                </span>
              </button>
            </div>
          </div>

          <!-- Создание нового навыка -->
          <div
            v-else
            key="empty"
            class="empty-state"
          >
            <div class="empty-illustration">
              <svg
                viewBox="0 0 96 96"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="43"
                  cy="43"
                  r="26"
                  class="empty-circle"
                />

                <path
                  d="M62 62L81 81"
                  class="empty-search-line"
                />

                <path
                  d="M34 43H52M43 34V52"
                  class="empty-plus"
                />

                <path
                  d="M68 18V28M63 23H73"
                  class="empty-decoration"
                />
              </svg>
            </div>

            <h3 class="state-title">
              Такого навыка пока нет
            </h3>

            <p class="state-description">
              Вы можете добавить
              <strong>«{{ normalizedSearchQuery }}»</strong>
              в каталог навыков HR3.
            </p>

            <button
              type="button"
              class="create-skill-button"
              :disabled="isCreatingSkill"
              @click="addSkill"
            >
              <span
                v-if="isCreatingSkill"
                class="button-spinner"
              ></span>

              <svg
                v-else
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 5V19M5 12H19"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>

              <span>
                {{ isCreatingSkill ? "Добавляем..." : "Создать новый навык" }}
              </span>
            </button>

            <p class="create-skill-hint">
              Новый навык станет доступен другим пользователям HR3.
            </p>
          </div>
        </Transition>
      </div>
    </div>

    <ErrorToast
      v-if="errorMessage"
      :message="errorMessage"
    />

    <WarningToast
      v-if="warningMessage"
      :message="warningMessage"
    />

    <Snackbar
      message="Навыки успешно сохранены!"
      type="success"
      :show="successSnackbar"
    />
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineExpose,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";

import debounce from "lodash/debounce";

import HashtagService from "../../api/HashtagService.js";

import ErrorToast from "../../components/ErrorToast.vue";
import WarningToast from "../../components/WarningToast.vue";
import Snackbar from "../../components/Snackbar.vue";

const emit = defineEmits(["save-hashtags"]);

const searchInputRef = ref(null);

const searchQuery = ref("");
const skills = ref([]);
const selectedSkills = ref([]);

const isSearching = ref(false);
const isSaving = ref(false);
const isCreatingSkill = ref(false);
const isSearchFocused = ref(false);

const successSnackbar = ref(false);
const errorMessage = ref("");
const warningMessage = ref("");

const skillExamples = [
  "Swift",
  "Python",
  "Figma",
  "Продажи",
];

let currentSearchRequestId = 0;
let successTimer = null;
let errorTimer = null;
let warningTimer = null;

const normalizedSearchQuery = computed(() => {
  return searchQuery.value.trim();
});

const hasSearchQuery = computed(() => {
  return normalizedSearchQuery.value.length > 0;
});

const visibleSkills = computed(() => {
  return skills.value.filter((skill) => {
    return skill?.id && skill?.name;
  });
});

const selectedSkillsCountText = computed(() => {
  return getCountText(
    selectedSkills.value.length,
    "навык",
    "навыка",
    "навыков"
  );
});

const resultsCountText = computed(() => {
  return getCountText(
    visibleSkills.value.length,
    "навык",
    "навыка",
    "навыков"
  );
});

const getCountText = (count, one, few, many) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;

  let word = many;

  if (lastTwoDigits < 11 || lastTwoDigits > 14) {
    if (lastDigit === 1) {
      word = one;
    } else if (lastDigit >= 2 && lastDigit <= 4) {
      word = few;
    }
  }

  return `${count} ${word}`;
};

const normalizeSkillsResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.hashtags)) {
    return response.hashtags;
  }

  if (Array.isArray(response?.skills)) {
    return response.skills;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const normalizeSkill = (skill) => {
  if (!skill) {
    return null;
  }

  const id =
    skill.id ??
    skill.hashtag_id ??
    skill.skill_id ??
    skill.document_id ??
    null;

  const name =
    skill.name ??
    skill.title ??
    skill.hashtag ??
    skill.skill ??
    "";

  if (!id || !String(name).trim()) {
    return null;
  }

  return {
    ...skill,
    id,
    name: String(name).trim(),
  };
};

const normalizeSkills = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  const uniqueSkills = new Map();

  items.forEach((item) => {
    const normalizedSkill = normalizeSkill(item);

    if (!normalizedSkill) {
      return;
    }

    uniqueSkills.set(normalizedSkill.id, normalizedSkill);
  });

  return Array.from(uniqueSkills.values());
};

const clearMessages = () => {
  errorMessage.value = "";
  warningMessage.value = "";
};

const showError = (message) => {
  window.clearTimeout(errorTimer);

  errorMessage.value = "";

  nextTick(() => {
    errorMessage.value = message;
  });

  errorTimer = window.setTimeout(() => {
    errorMessage.value = "";
  }, 4500);
};

const showWarning = (message) => {
  window.clearTimeout(warningTimer);

  warningMessage.value = "";

  nextTick(() => {
    warningMessage.value = message;
  });

  warningTimer = window.setTimeout(() => {
    warningMessage.value = "";
  }, 3500);
};

const showSuccess = () => {
  window.clearTimeout(successTimer);

  successSnackbar.value = false;

  nextTick(() => {
    successSnackbar.value = true;
  });

  successTimer = window.setTimeout(() => {
    successSnackbar.value = false;
  }, 2500);
};

const isSelected = (skillId) => {
  return selectedSkills.value.some((skill) => {
    return skill.id === skillId;
  });
};

const toggleSkill = (skill) => {
  clearMessages();

  if (isSelected(skill.id)) {
    removeSkill(skill.id);
    return;
  }

  selectedSkills.value.push(skill);
};

const removeSkill = (skillId) => {
  selectedSkills.value = selectedSkills.value.filter((skill) => {
    return skill.id !== skillId;
  });
};

const clearSelectedSkills = () => {
  selectedSkills.value = [];
};

const rawSearchSkills = async () => {
  const query = normalizedSearchQuery.value;
  const requestId = ++currentSearchRequestId;

  if (!query) {
    skills.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;
  errorMessage.value = "";

  try {
    const response = await HashtagService.searchHashtags(query);

    if (requestId !== currentSearchRequestId) {
      return;
    }

    const receivedSkills = normalizeSkillsResponse(response);

    skills.value = normalizeSkills(receivedSkills);

    console.log("📡 Найденные навыки:", skills.value);
  } catch (error) {
    if (requestId !== currentSearchRequestId) {
      return;
    }

    skills.value = [];

    console.error(
      "❌ Ошибка при поиске навыков:",
      error?.response?.data || error?.message || error
    );

    showError("Не удалось выполнить поиск навыков.");
  } finally {
    if (requestId === currentSearchRequestId) {
      isSearching.value = false;
    }
  }
};

const debouncedSearch = debounce(rawSearchSkills, 350);

const handleSearchInput = () => {
  clearMessages();

  if (!normalizedSearchQuery.value) {
    currentSearchRequestId += 1;
    debouncedSearch.cancel();

    skills.value = [];
    isSearching.value = false;

    return;
  }

  isSearching.value = true;
  debouncedSearch();
};

const clearSearch = () => {
  currentSearchRequestId += 1;
  debouncedSearch.cancel();

  searchQuery.value = "";
  skills.value = [];
  isSearching.value = false;

  clearMessages();

  focusSearch();
};

const focusSearch = async () => {
  await nextTick();
  searchInputRef.value?.focus();
};

const searchExample = async (example) => {
  searchQuery.value = example;
  await nextTick();

  handleSearchInput();
  focusSearch();
};

const handleEnterPress = () => {
  if (isSearching.value || isCreatingSkill.value) {
    return;
  }

  if (visibleSkills.value.length === 1) {
    toggleSkill(visibleSkills.value[0]);
    return;
  }

  if (
    hasSearchQuery.value &&
    visibleSkills.value.length === 0
  ) {
    addSkill();
  }
};

const addSkill = async () => {
  const skillName = normalizedSearchQuery.value;

  if (!skillName || isCreatingSkill.value) {
    return;
  }

  const alreadySelected = selectedSkills.value.some((skill) => {
    return skill.name.toLocaleLowerCase() === skillName.toLocaleLowerCase();
  });

  if (alreadySelected) {
    showWarning("Этот навык уже добавлен.");
    return;
  }

  isCreatingSkill.value = true;
  clearMessages();

  try {
    const response = await HashtagService.addHashtag(skillName);

    const newSkill = normalizeSkill(
      response?.hashtag ??
      response?.skill ??
      response?.data ??
      response
    );

    if (!newSkill) {
      throw new Error("Сервис не вернул созданный навык");
    }

    if (!isSelected(newSkill.id)) {
      selectedSkills.value.push(newSkill);
    }

    if (!skills.value.some((skill) => skill.id === newSkill.id)) {
      skills.value.push(newSkill);
    }

    searchQuery.value = "";
    skills.value = [];

    currentSearchRequestId += 1;
    debouncedSearch.cancel();

    console.log("✅ Новый навык добавлен:", newSkill);
  } catch (error) {
    console.error(
      "❌ Ошибка при создании навыка:",
      error?.response?.data || error?.message || error
    );

    showError("Не удалось создать новый навык.");
  } finally {
    isCreatingSkill.value = false;
  }
};

const updateUserSkills = async () => {
  const response = await HashtagService.updateUserHashtags(
    selectedSkills.value
  );

  /*
   Некоторые сервисы возвращают:
   - true;
   - объект { success: true };
   - созданные данные;
   - undefined при успешном запросе.

   Явным провалом считаем только false или success: false.
  */
  if (response === false || response?.success === false) {
    throw new Error("Сервис вернул ошибку сохранения навыков");
  }

  return true;
};

const saveSkills = async () => {
  if (isSaving.value) {
    return false;
  }

  if (selectedSkills.value.length === 0) {
    showWarning("Добавьте хотя бы один ключевой навык.");
    return false;
  }

  isSaving.value = true;
  clearMessages();

  try {
    await updateUserSkills();

    showSuccess();

    emit("save-hashtags", {
      skills: [...selectedSkills.value],
      hashtags: [...selectedSkills.value],
    });

    console.log("✅ Навыки пользователя успешно обновлены!");

    return true;
  } catch (error) {
    console.error(
      "❌ Ошибка при сохранении навыков:",
      error?.response?.data || error?.message || error
    );

    showError("Не удалось сохранить навыки. Попробуйте позже.");

    return false;
  } finally {
    isSaving.value = false;
  }
};

const handleSaveSkillsEvent = () => {
  saveSkills();
};

watch(isSaving, (isBlocking) => {
  document.body.classList.toggle(
    "skills-loading",
    isBlocking
  );
});

onMounted(() => {
  /*
   Оставляем старое имя события, чтобы Registration.vue
   продолжил работать без изменений.
  */
  document.addEventListener(
    "save-hashtags",
    handleSaveSkillsEvent
  );

  console.log("🟢 ChooseSkills подписан на save-hashtags");
});

onUnmounted(() => {
  document.removeEventListener(
    "save-hashtags",
    handleSaveSkillsEvent
  );

  debouncedSearch.cancel();
  currentSearchRequestId += 1;

  window.clearTimeout(successTimer);
  window.clearTimeout(errorTimer);
  window.clearTimeout(warningTimer);

  document.body.classList.remove("skills-loading");

  console.log("🔴 ChooseSkills отписан от save-hashtags");
});

defineExpose({
  saveHashtags: saveSkills,
  saveSkills,
  selectedSkills,
});
</script>

<style scoped>
.skills-container {
  --accent: #2887f7;
  --accent-hover: #1675e8;
  --accent-soft: rgba(40, 135, 247, 0.12);
  --accent-border: rgba(40, 135, 247, 0.3);

  --surface-primary: rgba(255, 255, 255, 0.055);
  --surface-secondary: rgba(255, 255, 255, 0.035);
  --surface-hover: rgba(255, 255, 255, 0.075);
  --surface-selected: rgba(40, 135, 247, 0.11);

  --border-primary: rgba(255, 255, 255, 0.1);
  --border-secondary: rgba(255, 255, 255, 0.065);

  --text-primary: rgba(255, 255, 255, 0.96);
  --text-secondary: rgba(255, 255, 255, 0.62);
  --text-tertiary: rgba(255, 255, 255, 0.4);

  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  position: relative;
}

[data-theme="light"] .skills-container {
  --surface-primary: #ffffff;
  --surface-secondary: #f7f9fc;
  --surface-hover: #f2f6fb;
  --surface-selected: rgba(40, 135, 247, 0.08);

  --border-primary: rgba(26, 43, 68, 0.11);
  --border-secondary: rgba(26, 43, 68, 0.07);

  --text-primary: #172033;
  --text-secondary: #657086;
  --text-tertiary: #929caf;
}

.skills-content {
  width: 100%;
  max-width: 900px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

/* Header */

.skills-header {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 27px;
}

.header-icon {
  width: 54px;
  height: 54px;
  flex: 0 0 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.18),
      rgba(40, 135, 247, 0.07)
    );
  border: 1px solid rgba(40, 135, 247, 0.2);
  border-radius: 17px;
  box-shadow:
    0 10px 30px rgba(40, 135, 247, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.header-icon svg {
  width: 28px;
  height: 28px;
}

.header-text {
  min-width: 0;
}

.skills-title {
  margin: 1px 0 7px;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 26px;
  line-height: 1.2;
  letter-spacing: -0.45px;
  color: var(--text-primary);
}

.skills-description {
  max-width: 620px;
  margin: 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: var(--text-secondary);
}

/* Selected skills */

.selected-skills-section {
  width: 100%;
  max-width: 680px;
  padding: 17px;
  margin-bottom: 20px;
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(40, 135, 247, 0.035)
    );
  border: 1px solid var(--accent-border);
  border-radius: 20px;
}

.selected-skills-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 13px;
}

.section-title {
  margin: 0 0 3px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  color: var(--text-primary);
}

.section-subtitle {
  margin: 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  color: var(--text-tertiary);
}

.clear-all-button {
  padding: 7px 9px;
  color: var(--text-secondary);
  background: transparent;
  border: none;
  border-radius: 9px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease;
}

.clear-all-button:hover {
  color: var(--text-primary);
  background: var(--surface-hover);
}

.selected-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-skill {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 8px 7px 13px;
  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #2887f7,
      #1675e8
    );
  border: none;
  border-radius: 12px;
  box-shadow: 0 7px 17px rgba(40, 135, 247, 0.2);
  font-family: "Eastman-Medium", sans-serif;
  cursor: pointer;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}

.selected-skill:hover {
  transform: translateY(-1px);
  box-shadow: 0 9px 21px rgba(40, 135, 247, 0.28);
}

.selected-skill-name {
  max-width: 220px;
  overflow: hidden;
  font-size: 13px;
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.remove-icon {
  width: 23px;
  height: 23px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.14);
  border-radius: 7px;
  transition:
    color 0.18s ease,
    background 0.18s ease;
}

.selected-skill:hover .remove-icon {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.22);
}

.remove-icon svg {
  width: 13px;
  height: 13px;
}

/* Search */

.search-section {
  width: 100%;
  max-width: 680px;
  margin-bottom: 22px;
}

.search-bar {
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 0 17px;
  color: var(--text-tertiary);
  background: var(--surface-primary);
  border: 1px solid var(--border-primary);
  border-radius: 18px;
  box-shadow:
    0 8px 28px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.035);
  transition:
    color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.search-bar:hover {
  background: var(--surface-hover);
  border-color: rgba(40, 135, 247, 0.18);
}

.search-bar.focused {
  color: var(--accent);
  border-color: rgba(40, 135, 247, 0.68);
  box-shadow:
    0 0 0 4px rgba(40, 135, 247, 0.11),
    0 12px 32px rgba(0, 0, 0, 0.1);
}

.search-icon {
  width: 21px;
  height: 21px;
  flex: 0 0 21px;
}

.search-input {
  min-width: 0;
  flex: 1;
  padding: 0;
  color: var(--text-primary);
  background: transparent;
  border: none;
  outline: none;
  caret-color: var(--accent);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 15px;
  line-height: 1;
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.clear-search-button {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  color: var(--text-secondary);
  background: var(--surface-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 50%;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease,
    transform 0.18s ease;
}

.clear-search-button:hover {
  color: var(--text-primary);
  background: var(--surface-hover);
  transform: scale(1.05);
}

.clear-search-button svg {
  width: 15px;
  height: 15px;
}

.search-spinner {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  border: 2px solid rgba(40, 135, 247, 0.22);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.72s linear infinite;
}

.search-hint {
  margin: 9px 3px 0;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-tertiary);
}

/* Main state */

.skills-state-container {
  width: 100%;
  flex: 1;
  position: relative;
}

.initial-state,
.empty-state {
  width: 100%;
  max-width: 680px;
  min-height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 34px 24px 42px;
  text-align: center;
  background:
    radial-gradient(
      circle at 50% 25%,
      rgba(40, 135, 247, 0.07),
      transparent 48%
    );
  border: 1px dashed var(--border-primary);
  border-radius: 24px;
}

.initial-illustration {
  width: 195px;
  height: 106px;
  position: relative;
  margin-bottom: 20px;
}

.illustration-center {
  width: 57px;
  height: 57px;
  position: absolute;
  top: 24px;
  left: 69px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background:
    linear-gradient(
      145deg,
      #3591fa,
      #1675e8
    );
  border: 6px solid var(--surface-primary);
  border-radius: 19px;
  box-shadow: 0 12px 28px rgba(40, 135, 247, 0.28);
  transform: rotate(-4deg);
}

.illustration-center svg {
  width: 27px;
  height: 27px;
}

.illustration-chip {
  position: absolute;
  display: inline-flex;
  align-items: center;
  min-height: 29px;
  padding: 6px 10px;
  color: var(--text-secondary);
  background: var(--surface-primary);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  box-shadow: 0 8px 19px rgba(0, 0, 0, 0.08);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  white-space: nowrap;
}

.chip-one {
  top: 4px;
  left: 0;
  transform: rotate(-7deg);
}

.chip-two {
  top: 2px;
  right: 3px;
  transform: rotate(6deg);
}

.chip-three {
  left: 4px;
  bottom: 5px;
  transform: rotate(5deg);
}

.chip-four {
  right: 0;
  bottom: 7px;
  transform: rotate(-5deg);
}

.state-title {
  margin: 0 0 8px;
  color: var(--text-primary);
  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.3;
}

.state-description {
  max-width: 420px;
  margin: 0 0 21px;
  color: var(--text-secondary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.55;
}

.state-description strong {
  color: var(--text-primary);
  font-family: "Eastman-Medium", sans-serif;
}

.examples {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 7px;
}

.examples-label {
  margin-right: 2px;
  color: var(--text-tertiary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
}

.example-skill {
  min-height: 31px;
  padding: 6px 10px;
  color: var(--text-secondary);
  background: var(--surface-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 10px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  cursor: pointer;
  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.example-skill:hover {
  color: var(--accent);
  background: var(--accent-soft);
  border-color: var(--accent-border);
  transform: translateY(-1px);
}

/* Search results */

.search-loading-state,
.search-results-state {
  width: 100%;
  max-width: 680px;
}

.results-heading {
  min-height: 43px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 15px;
}

.results-title {
  margin: 0 0 3px;
  color: var(--text-primary);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
}

.results-subtitle {
  margin: 0;
  color: var(--text-tertiary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

.searching-dots {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 5px;
}

.searching-dots span {
  width: 5px;
  height: 5px;
  background: var(--accent);
  border-radius: 50%;
  animation: searching-dot 1.1s infinite ease-in-out;
}

.searching-dots span:nth-child(2) {
  animation-delay: 0.14s;
}

.searching-dots span:nth-child(3) {
  animation-delay: 0.28s;
}

/* Skill result items */

.skills-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.skill-item {
  width: 100%;
  min-height: 59px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 13px;
  color: var(--text-primary);
  background: var(--surface-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease,
    box-shadow 0.18s ease;
}

.skill-item:hover {
  background: var(--surface-hover);
  border-color: rgba(40, 135, 247, 0.25);
  transform: translateY(-1px);
  box-shadow: 0 9px 21px rgba(0, 0, 0, 0.07);
}

.skill-item.selected {
  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.14),
      rgba(40, 135, 247, 0.06)
    );
  border-color: var(--accent-border);
}

.skill-item-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  background: var(--surface-secondary);
  border: 1px solid var(--border-secondary);
  border-radius: 11px;
  transition:
    color 0.18s ease,
    background 0.18s ease,
    border-color 0.18s ease;
}

.skill-item.selected .skill-item-icon {
  color: #ffffff;
  background: var(--accent);
  border-color: var(--accent);
  box-shadow: 0 6px 15px rgba(40, 135, 247, 0.24);
}

.skill-item-icon svg {
  width: 17px;
  height: 17px;
}

.skill-item-name {
  min-width: 0;
  flex: 1;
  overflow: hidden;
  color: var(--text-primary);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 14px;
  line-height: 1.35;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.skill-item-status {
  color: var(--text-tertiary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  transition: color 0.18s ease;
}

.skill-item:hover .skill-item-status,
.skill-item.selected .skill-item-status {
  color: var(--accent);
}

/* Skeleton */

.skills-skeleton-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.skill-skeleton {
  height: 42px;
  border-radius: 13px;
}

.skeleton-width-1 {
  width: 112px;
}

.skeleton-width-2 {
  width: 155px;
}

.skeleton-width-3 {
  width: 94px;
}

.skeleton-width-4 {
  width: 176px;
}

.skeleton-width-5 {
  width: 130px;
}

.skeleton-width-6 {
  width: 104px;
}

.skeleton-width-7 {
  width: 162px;
}

.skeleton-width-8 {
  width: 121px;
}

.skeleton-block {
  overflow: hidden;
  position: relative;
  background: var(--border-primary);
}

.skeleton-block::after {
  content: "";
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.11),
      transparent
    );
  transform: translateX(-100%);
  animation: shimmer 1.4s infinite;
}

[data-theme="light"] .skeleton-block::after {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.75),
      transparent
    );
}

/* Empty state */

.empty-illustration {
  width: 105px;
  height: 105px;
  margin-bottom: 15px;
}

.empty-illustration svg {
  width: 100%;
  height: 100%;
}

.empty-circle {
  fill: var(--surface-primary);
  stroke: var(--border-primary);
  stroke-width: 2;
}

.empty-search-line {
  stroke: var(--accent);
  stroke-width: 5;
  stroke-linecap: round;
}

.empty-plus {
  stroke: var(--text-secondary);
  stroke-width: 3;
  stroke-linecap: round;
}

.empty-decoration {
  stroke: var(--accent);
  stroke-width: 2.5;
  stroke-linecap: round;
}

.create-skill-button {
  min-height: 45px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 0 17px;
  color: #ffffff;
  background:
    linear-gradient(
      135deg,
      #2887f7,
      #1675e8
    );
  border: none;
  border-radius: 14px;
  box-shadow: 0 9px 21px rgba(40, 135, 247, 0.24);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  cursor: pointer;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}

.create-skill-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 25px rgba(40, 135, 247, 0.3);
}

.create-skill-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.create-skill-button svg {
  width: 18px;
  height: 18px;
}

.create-skill-hint {
  margin: 10px 0 0;
  color: var(--text-tertiary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

.button-spinner {
  width: 17px;
  height: 17px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* Saving overlay */

.loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(7, 12, 22, 0.58);
  backdrop-filter: blur(9px);
  -webkit-backdrop-filter: blur(9px);
}

.saving-card {
  min-width: 310px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 22px;
  background: rgba(28, 34, 47, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 20px;
  box-shadow: 0 24px 65px rgba(0, 0, 0, 0.3);
}

[data-theme="light"] .saving-card {
  background: rgba(255, 255, 255, 0.97);
  border-color: rgba(20, 38, 65, 0.1);
}

.saving-spinner {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  border: 4px solid rgba(40, 135, 247, 0.2);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

.saving-content {
  min-width: 0;
}

.saving-title {
  margin: 0 0 4px;
  color: var(--text-primary);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 15px;
}

.saving-subtitle {
  margin: 0;
  color: var(--text-secondary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

/* Transitions */

.content-fade-enter-active,
.content-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.content-fade-enter-from {
  opacity: 0;
  transform: translateY(7px);
}

.content-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.selected-section-enter-active,
.selected-section-leave-active {
  overflow: hidden;
  transition:
    opacity 0.24s ease,
    transform 0.24s ease,
    max-height 0.3s ease,
    margin 0.3s ease,
    padding 0.3s ease;
}

.selected-section-enter-from,
.selected-section-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
  opacity: 0;
  transform: translateY(-8px);
}

.selected-section-enter-to,
.selected-section-leave-from {
  max-height: 400px;
}

.skill-chip-enter-active,
.skill-chip-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.skill-chip-enter-from {
  opacity: 0;
  transform: scale(0.75) translateY(5px);
}

.skill-chip-leave-to {
  opacity: 0;
  transform: scale(0.75);
}

.skill-chip-move {
  transition: transform 0.22s ease;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Animations */

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes searching-dot {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }

  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}

/* Mobile */

@media (max-width: 768px) {
  .skills-container {
    width: 100%;
    padding: 0;
  }

  .skills-content {
    max-width: none;
  }

  .skills-header {
    gap: 13px;
    margin-bottom: 23px;
  }

  .header-icon {
    width: 46px;
    height: 46px;
    flex-basis: 46px;
    border-radius: 15px;
  }

  .header-icon svg {
    width: 24px;
    height: 24px;
  }

  .skills-title {
    margin-top: 0;
    font-size: 21px;
    letter-spacing: -0.3px;
  }

  .skills-description {
    font-size: 13px;
    line-height: 1.48;
  }

  .selected-skills-section,
  .search-section,
  .initial-state,
  .empty-state,
  .search-loading-state,
  .search-results-state {
    max-width: none;
  }

  .selected-skills-section {
    padding: 14px;
    border-radius: 17px;
  }

  .selected-skills {
    gap: 7px;
  }

  .selected-skill {
    min-height: 34px;
    padding: 6px 7px 6px 11px;
    border-radius: 11px;
  }

  .selected-skill-name {
    max-width: 180px;
    font-size: 12px;
  }

  .search-bar {
    min-height: 53px;
    padding: 0 15px;
    border-radius: 16px;
  }

  .search-input {
    font-size: 16px;
  }

  .search-hint {
    padding: 0 3px;
    font-size: 11px;
  }

  .initial-state,
  .empty-state {
    min-height: 300px;
    padding: 30px 18px 38px;
    border-radius: 20px;
  }

  .state-title {
    font-size: 17px;
  }

  .state-description {
    font-size: 12px;
  }

  .skills-skeleton-list {
    gap: 8px;
  }

  .skill-skeleton {
    height: 39px;
  }

  .skill-item {
    min-height: 56px;
    padding: 9px 11px;
    border-radius: 15px;
  }

  .skill-item-status {
    display: none;
  }

  .saving-card {
    width: 100%;
    min-width: 0;
    max-width: 360px;
  }
}

@media (max-width: 420px) {
  .skills-header {
    align-items: center;
  }

  .skills-description {
    display: none;
  }

  .skills-title {
    font-size: 20px;
  }

  .clear-all-button {
    padding-right: 3px;
  }

  .initial-illustration {
    transform: scale(0.9);
    margin-bottom: 11px;
  }

  .examples-label {
    width: 100%;
    margin: 0 0 2px;
  }

  .selected-skill-name {
    max-width: 145px;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>

<style>
body.skills-loading {
  overflow: hidden;
}
</style>